/**
 * WP-2: pure sorting/classification logic for the desktop Aankomend panel
 * (components/UpcomingPanel.jsx). Kept dependency-free (just `daysUntil`)
 * so it's unit-testable with node:test — see upcoming.test.mjs — without
 * spinning up Preact/DOM.
 */
import { daysUntil } from './format.js';

function isOverdueHard(task) {
  if (!task.deadline || task.deadline_type !== 'hard') return false;
  const days = daysUntil(task.deadline);
  return days !== null && days < 0;
}

/**
 * Brief-specified order (WP-2 spec): 1) overdue hard deadlines, then
 * 2) any other task with a deadline, ascending by date (hard before soft
 * on the same date), then 3) no-deadline tasks by priority DESC, then
 * created_at ASC.
 *
 * Group 1 is a genuine separate rule, not an artifact of a plain ascending
 * date sort: an overdue *soft* deadline can be chronologically earlier
 * (more overdue) than an overdue *hard* one, but the hard one must still
 * rank first — group 1 is an absolute priority boost, independent of how
 * overdue anything in group 2 is.
 */
export function sortUpcomingTasks(tasks) {
  return [...tasks].sort((a, b) => {
    const aOverdueHard = isOverdueHard(a);
    const bOverdueHard = isOverdueHard(b);
    if (aOverdueHard !== bOverdueHard) return aOverdueHard ? -1 : 1;
    if (aOverdueHard && bOverdueHard) {
      return (a.deadline || '').localeCompare(b.deadline || '');
    }

    const aHasDeadline = !!a.deadline;
    const bHasDeadline = !!b.deadline;
    if (aHasDeadline !== bHasDeadline) return aHasDeadline ? -1 : 1;
    if (aHasDeadline && bHasDeadline) {
      const cmp = a.deadline.localeCompare(b.deadline);
      if (cmp !== 0) return cmp;
      // Same date: hard before everything else, all non-hard types equal.
      // Rank-based on purpose — deadline_type can be NULL/'' on real rows
      // (update_task() has no create_task()-style 'soft' backfill), and the
      // earlier `a !== b` version returned 1 for BOTH compare(a,b) and
      // compare(b,a) when the two differing types were both non-hard
      // ('soft' vs NULL), violating comparator antisymmetry — which is
      // engine-defined sort behavior, i.e. none at all (WP-2 review
      // finding, verified by reproduction).
      const aRank = a.deadline_type === 'hard' ? 0 : 1;
      const bRank = b.deadline_type === 'hard' ? 0 : 1;
      return aRank - bRank;
    }

    const aPriority = Number(a.priority) || 0;
    const bPriority = Number(b.priority) || 0;
    if (aPriority !== bPriority) return bPriority - aPriority;
    return (a.created_at || '').localeCompare(b.created_at || '');
  });
}

/**
 * Display-only "should this deadline badge render red" heuristic for the
 * panel's compact list. Deliberately NOT a reimplementation of
 * ETM_Engine's precise at-risk/warnings math (roadmap §5's slack-based
 * hard-deadline ramp) — that computation needs available_minutes context
 * this plain `GET /tasks?status=active` list doesn't carry, and CLAUDE.md
 * is explicit that roadmap §5 formulas live in one place, not improvised
 * elsewhere. This only flags the unambiguous cases for a summary list:
 * anything overdue (any deadline type), or a hard deadline due today.
 */
export function isDeadlineAtRisk(task) {
  if (!task.deadline) return false;
  const days = daysUntil(task.deadline);
  if (days === null) return false;
  if (days < 0) return true;
  return task.deadline_type === 'hard' && days === 0;
}
